/**
 * Where kitchen pendants may hang: the centreline of a counter people SIT at,
 * as a line segment in world XZ, plus how many fixtures that segment carries.
 *
 * Pure — no I/O, no THREE. Consumed by `kitchenLighting.planPendants`.
 *
 * ## Islands and peninsulas are not symmetric
 *
 * An ISLAND is one countertop slab carrying `category.island`. `buildIsland`
 * already folds any seating overhang into that slab's own `dimensions` and
 * shifts its `center` toward the seats, so the slab IS the eating surface and
 * its long axis is the anchor.
 *
 * A PENINSULA is a wall run, and at most ONE of its legs is ever seated:
 * `keepBestSeatedLeg` (`materializeCandidate`) reduces the per-leg seating map
 * to a single entry before anything is emitted. So "which leg has the seating"
 * needs no heuristic and no depth probe — it is the leg whose top says so.
 *
 * Since generator 1.99.0 that top is ONE slab, knee overhang included, and it
 * says so itself: `seating` (`seatedPeninsulaTop.js`). A range on the leg
 * cuts it into pieces with a bar behind the range, each carrying `seating`,
 * all on the same dining edge — so the pieces of one edge are read as one
 * eating surface, as the knee strip was: its whole seated span, centred
 * between the dining edge and the counter's front.
 *
 * ## A kitchen applied before 1.99.0 has the knee STRIP
 *
 * A `peninsula_seating_overhang_*` slab with no `seating`, 12" deep, OUTBOARD
 * of the back panel — a pendant centred on it hangs over the diner's lap, not
 * over the counter. It supplies the seated SPAN and the leg's frame; the
 * counter run on the same leg supplies the depth. The pendant line is the
 * centreline of the two together, the same line the one slab gives.
 *
 * ## Handedness
 *
 * Every kitchen builder stores `rotation.y = atan2(normal.x, normal.z)`, which
 * puts local +X at `(cos ry, -sin ry)` and local +Z (the inward normal) at
 * `(sin ry, cos ry)` — see `buildIsland.js`'s `along` and `shared-lib/
 * geometry/objectFootprint.js`. A `(cos, sin)` long axis is the MIRROR of that
 * and agrees only at 0/90/180/270; at 30 degrees it is out by up to a metre.
 */

const { objectFootprint } = require('../../geometry/objectFootprint');
const { objectWallIds } = require('../../cabinet-runs/objectWallIds');
const {
  doPolygonsOverlap,
} = require('../../bathroom-analyzer/pipeline/geometry');

// Minimum clear air between two adjacent pendant shades. Below this a row
// reads as one cluster rather than as separate fixtures.
const PENDANT_MIN_CLEAR_GAP_M = 0.457; // 18"
// Minimum clear air between the outermost shade and the end of the span, so a
// pendant never overhangs the counter it lights.
const PENDANT_END_MARGIN_M = 0.229; // 9"
// Most fixtures one counter carries, however long it is. Beyond three the row
// reads as track lighting.
const PENDANT_MAX_COUNT = 3;
// Where a kitchen pendant's shade bottom lands, above the finished floor. The
// trade rule is 30-36" of air between the shade and the counter it lights: low
// enough to light the work surface, high enough that someone standing sees
// across the island beneath it. 72" is 36" over a standard 36" counter and 30"
// over a 42" bar top, so one floor-relative number sits inside the rule for
// both. Shared by the Fixtures & Finishes planner and `style_kitchen`.
const KITCHEN_PENDANT_BOTTOM_AFF_INCHES = 72;

function typeOf(o) {
  return (o?.type || o?.objectType || '').toLowerCase();
}
function cat(o, k) {
  return Boolean(
    o?.category && typeof o.category === 'object' && o.category[k]
  );
}
function isCountertop(o) {
  return typeOf(o) === 'countertop' || cat(o, 'countertop');
}
function dimY(o) {
  return o?.dimensions?.y ?? o?.dimensions?.height ?? 0;
}
function topY(o) {
  return (o?.center?.y ?? 0) + dimY(o) / 2;
}

/**
 * Long-axis unit direction (world XZ) and its length, in the handedness every
 * kitchen builder stores rotations in.
 */
function longAxisOf(rotY, dims) {
  const along = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  const across = { x: Math.sin(rotY), z: Math.cos(rotY) };
  const dx = dims?.x ?? 0;
  const dz = dims?.z ?? 0;
  if (dx >= dz) return { dir: along, length: dx, normal: across };
  return { dir: across, length: dz, normal: { x: -along.x, z: -along.z } };
}

/** Inward normal (local +Z) for a stored `rotation.y`. */
function inwardNormalOf(rotY) {
  return { x: Math.sin(rotY), z: Math.cos(rotY) };
}

/** True when two stored rotations describe the same line (mod 180 degrees). */
function isParallel(rotA, rotB, tolRad = 0.05) {
  const d = Math.abs(((rotA - rotB) % Math.PI) + Math.PI) % Math.PI;
  return d < tolRad || Math.PI - d < tolRad;
}

/**
 * Counter runs belonging to the same peninsula leg as `slab`.
 *
 * Which field links a cabinet to its wall depends on who last emitted it:
 * the generator's apply persists `cabinetWallRefs` and strips `parentWallIds`,
 * the run engine's re-solve persists `parentWallIds` and no refs — so
 * `objectWallIds` reads both. (Reading the refs alone found no cabinet on a
 * peninsula the engine had re-solved, and the pendant row asked for a seated
 * peninsula over one with three pendants already hanging.) A run countertop
 * names neither, only `parents` naming its lower cabinets. So the chain is
 * slab -> cabinet-wall id -> cabinets on that wall -> countertops parented to
 * one of them.
 *
 * A corner cabinet belongs to BOTH legs of an L, which would drag the adjoining
 * leg's countertop in with it; the parallel test drops those, since the two
 * legs of an L meet at a right angle.
 */
function counterRunsOnLegOf(slab, objects) {
  const wallIds = new Set(objectWallIds(slab));
  if (!wallIds.size) return [];
  const cabinetIds = new Set(
    objects
      .filter((o) => objectWallIds(o).some((id) => wallIds.has(id)))
      .map((o) => o.id)
  );
  const slabRot = slab.rotation?.y ?? 0;
  return objects.filter(
    (o) =>
      isCountertop(o) &&
      !cat(o, 'seatingOverhang') &&
      isParallel(o.rotation?.y ?? 0, slabRot) &&
      (o.parents || []).some((p) => cabinetIds.has(p?.id))
  );
}

/**
 * The eating surface of a seated peninsula leg, as a pendant anchor.
 *
 * Depth is measured from the slab's OUTBOARD edge to the counter runs' INBOARD
 * edge, and the anchor sits at the middle of that. Returns null when the leg's
 * counter cannot be found — better no pendant than one hung over nothing.
 */
function peninsulaAnchorFor(slab, objects) {
  const runs = counterRunsOnLegOf(slab, objects);
  if (!runs.length) return null;

  const rotY = slab.rotation?.y ?? 0;
  const normal = inwardNormalOf(rotY);
  const slabCenter = slab.center || {};
  const alongNormal = (o) =>
    ((o.center?.x ?? 0) - (slabCenter.x ?? 0)) * normal.x +
    ((o.center?.z ?? 0) - (slabCenter.z ?? 0)) * normal.z;

  // Outboard face of the knee strip, and the far face of the deepest run.
  const outboardEdge = -(slab.dimensions?.z ?? 0) / 2;
  const inboardEdge = Math.max(
    ...runs.map((r) => alongNormal(r) + (r.dimensions?.z ?? 0) / 2)
  );
  if (!(inboardEdge > outboardEdge)) return null;
  const offset = (outboardEdge + inboardEdge) / 2;

  const { dir, length } = longAxisOf(rotY, slab.dimensions);
  return {
    sourceId: slab.id,
    kind: 'peninsula',
    center: {
      x: (slabCenter.x ?? 0) + normal.x * offset,
      z: (slabCenter.z ?? 0) + normal.z * offset,
    },
    dir,
    length,
    surfaceTopY: topY(slab),
  };
}

// Back edges of one dining edge, built from the same numbers, agree to well
// under a millimetre; a centimetre still parts two different counters.
const EDGE_TOL_M = 0.01;

/**
 * The seated slabs (`seating`) grouped by the dining edge they share: same
 * rotation, back edges on one line. A range splits a leg's top into pieces and
 * a bar, and a diner sees one counter.
 */
function seatedEdges(objects) {
  const edges = [];
  for (const o of objects) {
    if (!isCountertop(o) || !o.seating || !o.center || !o.dimensions) continue;
    const rotY = o.rotation?.y ?? 0;
    const normal = inwardNormalOf(rotY);
    const backAt =
      o.center.x * normal.x + o.center.z * normal.z - (o.dimensions.z ?? 0) / 2;
    const edge = edges.find(
      (e) =>
        Math.abs(Math.cos(e.rotY - rotY) - 1) < 1e-6 &&
        Math.abs(e.backAt - backAt) < EDGE_TOL_M
    );
    if (edge) edge.pieces.push(o);
    else edges.push({ rotY, normal, backAt, pieces: [o] });
  }
  return edges;
}

/**
 * A seated leg's eating surface, from its slabs: the whole seated span along
 * it, centred between the dining edge and the counter's front edge.
 */
function seatedEdgeAnchor(edge) {
  const { rotY, normal, backAt, pieces } = edge;
  const dir = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  let lo = Infinity;
  let hi = -Infinity;
  let front = -Infinity;
  for (const p of pieces) {
    const along = p.center.x * dir.x + p.center.z * dir.z;
    const across = p.center.x * normal.x + p.center.z * normal.z;
    lo = Math.min(lo, along - p.dimensions.x / 2);
    hi = Math.max(hi, along + p.dimensions.x / 2);
    front = Math.max(front, across + p.dimensions.z / 2);
  }
  if (!(hi > lo) || !(front > backAt)) return null;
  const mid = (lo + hi) / 2;
  const off = (backAt + front) / 2;
  // The deepest piece names the anchor — it is the counter, a bar is not.
  const lead = [...pieces].sort(
    (a, b) => b.dimensions.z - a.dimensions.z || b.dimensions.x - a.dimensions.x
  )[0];
  return {
    sourceId: lead.id,
    kind: 'peninsula',
    center: {
      x: dir.x * mid + normal.x * off,
      z: dir.z * mid + normal.z * off,
    },
    dir,
    length: hi - lo,
    surfaceTopY: topY(lead),
  };
}

function seatedEdgeAnchors(objects) {
  return seatedEdges(objects).map(seatedEdgeAnchor).filter(Boolean);
}

/** An island countertop is its own eating surface. */
function islandAnchorFor(slab) {
  const { dir, length } = longAxisOf(slab.rotation?.y ?? 0, slab.dimensions);
  if (!length) return null;
  return {
    sourceId: slab.id,
    kind: 'island',
    center: { x: slab.center?.x ?? 0, z: slab.center?.z ?? 0 },
    dir,
    length,
    surfaceTopY: topY(slab),
  };
}

/**
 * An island counter — a run standing clear of the walls — under EITHER of the
 * two vocabularies that describe one.
 *
 * `buildIsland` / `buildIslandWithSink` stamp `category.island`; the cabinet-run
 * engine (`shared-lib/cabinet-runs/freestanding.js`) stamps
 * `category.freestanding`. They mean the same thing, and which one a room
 * carries is not stable over its lifetime: any pass that re-solves the run —
 * `resolveRunIfApplianceResized`, which fires on every appliance swap, so on
 * every design-package apply — REPLACES `island_countertop_*`
 * (`countertop+island`) with `countertop_autorun_island_*`
 * (`countertop+freestanding`).
 *
 * Reading only `island` therefore worked until the user swapped an appliance,
 * and then reported "no island to go above" on a kitchen with an island plainly
 * in it. Matching both is a bridge over a real divergence, not a guard: the two
 * builders genuinely disagree about the word, and unifying them would have to
 * move every other reader (the recessed-can island skip, `mergeKitchenObjects`)
 * at the same time.
 */
function isIslandCounter(o) {
  return cat(o, 'island') || cat(o, 'freestanding');
}

/**
 * Every counter in the room that should carry pendants.
 *
 * Islands qualify on their own; a peninsula qualifies only through its seating
 * overhang, so an unseated peninsula — and the range-width "back counter
 * bridge", which is also a `countertop` tagged `peninsula` — contributes
 * nothing.
 */
function resolvePendantAnchors(objects = []) {
  const anchors = [...seatedEdgeAnchors(objects)];
  for (const o of objects) {
    if (!isCountertop(o)) continue;
    if (o.seating) continue; // read as a dining edge, above
    if (cat(o, 'seatingOverhang')) {
      const a = peninsulaAnchorFor(o, objects);
      if (a) anchors.push(a);
    } else if (isIslandCounter(o)) {
      const a = islandAnchorFor(o);
      if (a) anchors.push(a);
    }
  }
  return anchors;
}

/**
 * How many pendants a span carries, from the span and the fixture's own width.
 *
 * Fixtures sit at a fixed pitch of one shade plus the minimum gap, centred on
 * the span, so a wider pendant yields fewer of them on the same counter.
 */
function pendantCountFor(spanM, pendantWidthM) {
  if (!(spanM > 0) || !(pendantWidthM > 0)) return 0;
  const pitch = pendantWidthM + PENDANT_MIN_CLEAR_GAP_M;
  const usable = spanM - 2 * PENDANT_END_MARGIN_M + PENDANT_MIN_CLEAR_GAP_M;
  const fits = Math.floor(usable / pitch);
  return Math.max(1, Math.min(PENDANT_MAX_COUNT, fits));
}

/** Evenly-pitched offsets along the span, centred on it. */
function pendantOffsets(count, pendantWidthM) {
  const pitch = pendantWidthM + PENDANT_MIN_CLEAR_GAP_M;
  const offsets = [];
  for (let i = 0; i < count; i++) offsets.push((i - (count - 1) / 2) * pitch);
  return offsets;
}

/**
 * Objects that occupy the vertical band a pendant hangs in, so nothing is hung
 * into a vent hood, an upper cabinet or a refrigerator.
 *
 * The test is GEOMETRIC and FLOOR-RELATIVE. It deliberately does not reuse
 * `shared-lib/lighting/obstructions.js`: that module's `HIGH_MOUNT_MIN_BOTTOM`
 * compares an object's bottom against an absolute world Y, which is only a
 * mount height in a building whose floor sits at y = 0 — on a storey below
 * zero it classifies nothing as high-mounted, and the island hood it is meant
 * to catch reads as clear.
 *
 * A vent hood's duct is render-only (`dimensions.chimneyHeight`), outside its
 * bounding box, so it is added back here — the duct is the part of an island
 * hood that shares a pendant's air.
 */
function pendantObstacles(objects, bandBottomY, bandTopY) {
  const out = [];
  for (const o of objects || []) {
    if (!o?.center || !o?.dimensions) continue;
    if (o.demoed === true) continue;
    const t = typeOf(o);
    if (t.includes('light') || t.includes('chandelier')) continue;
    const bottom = (o.center.y ?? 0) - dimY(o) / 2;
    const top = topY(o) + (o.dimensions.chimneyHeight || 0);
    if (top <= bandBottomY || bottom >= bandTopY) continue;
    const fp = objectFootprint(o);
    if (fp) out.push(fp);
  }
  return out;
}

/**
 * Pendant positions for one anchor, with any that collide with an obstacle
 * dropped rather than nudged — a pendant slid off the counter's centreline
 * reads as a mistake, where a gap in the row reads as deliberate.
 */
function planPendantPositions(anchor, { pendantWidthM, obstacles = [] } = {}) {
  const count = pendantCountFor(anchor.length, pendantWidthM);
  if (!count) return [];
  const half = pendantWidthM / 2;
  const positions = [];
  for (const off of pendantOffsets(count, pendantWidthM)) {
    const p = {
      x: anchor.center.x + anchor.dir.x * off,
      z: anchor.center.z + anchor.dir.z * off,
    };
    const fp = objectFootprint({
      center: { x: p.x, z: p.z },
      dimensions: { x: pendantWidthM, z: pendantWidthM },
      rotation: { y: 0 },
    });
    if (obstacles.some((o) => doPolygonsOverlap(fp, o))) continue;
    positions.push({ ...p, halfWidth: half });
  }
  return positions;
}

module.exports = {
  resolvePendantAnchors,
  isIslandCounter,
  planPendantPositions,
  pendantCountFor,
  pendantOffsets,
  pendantObstacles,
  longAxisOf,
  PENDANT_MIN_CLEAR_GAP_M,
  PENDANT_END_MARGIN_M,
  PENDANT_MAX_COUNT,
  KITCHEN_PENDANT_BOTTOM_AFF_INCHES,
};
